import React, { useContext, useEffect, useState } from "react";
import DataTableSkeleton from "../shared/DataTableSkeleton";
import { Region } from "../shared/interfaces/Region";
import {
  DataTable,
  DataTableMultiSortMetaType,
  DataTableSortOrderType,
  DataTableSortParams,
} from "primereact/datatable";
import { Col, Row } from "react-bootstrap";
import { GlobalFilter } from "../shared/Grid/GlobalFilterTemplate";
import RefreshLoadingButton from "../shared/RefreshLoadingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../contexts/AuthContext";
import { Column } from "primereact/column";
import { TextTemplate } from "../shared/Grid/BodyTemplates";
import { Alert, AlertColor, ButtonGroup } from "@mui/material";
import { Link } from "react-router-dom";
import { usePage } from "../../routes";
import { SitesViewer } from "./SitesViewer";
import DeleteConfirmationButton from "../shared/FormLayout/Buttons/DeleteConfirmationButton";
import { useCompanyDelete } from "../data/Settings/useCompanyDelete";
import { Snackbar } from "../shared/Snackbar";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";
import { LOCALSTORAGE_KEYS } from "../shared/constants";

interface Props {
  customerSelected: boolean;
  customerId?: number;
  companiesData?: Region[];
  companiesDataLoading: boolean;
  companiesDataFetching: boolean;
  onDataRefreshClick: () => void;
  onNewCompanyClick: () => void;
  refreshCompanyData: () => void;
}

const sortFieldStorageKey = LOCALSTORAGE_KEYS.companiesSortField;
const sortOrderStorageKey = LOCALSTORAGE_KEYS.companiesSortOrder;

export function CompanyGrid(props: Props) {
  const { t } = useTranslation();
  const sitesViewerPath = usePage(SitesViewer.name)?.path;

  const { customerId } = props;

  const authContext = useContext(AuthContext);
  const userCanAddCompany = authContext.canI("add", "companies");
  const userCanDeleteCompany = authContext.canI("delete", "companies");

  const altRegionNames = useAlternativeRegionNames(customerId);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const companyDelete = useCompanyDelete({
    onSuccess: onDeleteCompanySuccess,
    onError: onDeleteCompanyError,
  });

  const [globalFilter, setGlobalFilter] = useState<string>("");

  function handleGlobalFilterInput(event: React.FormEvent<HTMLInputElement>): void {
    const element = event.target as HTMLInputElement;
    setGlobalFilter(element.value);
  }

  const [multiSortMeta, setMultiSortMeta] = useState<DataTableMultiSortMetaType>();

  const onSort = (e: DataTableSortParams) => {
    const field = e.multiSortMeta?.at(0)?.field as string;
    const order = e.multiSortMeta?.at(0)?.order;
    localStorage.setItem(sortFieldStorageKey, field);
    order && localStorage.setItem(sortOrderStorageKey, order?.toString());
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  };

  useEffect(() => {
    const field = localStorage.getItem(sortFieldStorageKey) as string;
    const order = localStorage.getItem(sortOrderStorageKey) as DataTableSortOrderType;
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  }, [props]);

  function handleConfirmDeleteCompanyClick(companyId: number) {
    userCanDeleteCompany && companyDelete.mutate(companyId);
  }

  function onDeleteCompanySuccess() {
    setSnackbarText(
      t("settings.companyDeleted", {
        val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
      }),
    );
    setSnackbarSeverity("success");
    setShowSnackbar(true);

    props.refreshCompanyData();
  }

  function onDeleteCompanyError(errorResponse: Response) {
    if (errorResponse.status === 403) {
      setSnackbarText(
        t("common.operationForbiddenError", {
          operation: t("common.delete"),
          item: altRegionNames.data?.companyAltName ?? t("common.company"),
        }),
      );
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } else {
      setSnackbarText(
        t("common.dataDeleteError", {
          val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
        }),
      );
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  }

  const handleErrorSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const headerToolbar = (
    <Row>
      <Col>
        {props.customerSelected && userCanAddCompany && (
          <Button
            variant="contained"
            color="primary"
            onClick={props.onNewCompanyClick}
            startIcon={<FontAwesomeIcon style={{ cursor: "pointer" }} icon={faPlus} />}
          >
            {altRegionNames.data?.companyAltName ?? t("common.company")}
          </Button>
        )}
      </Col>
      <Col className="d-flex justify-content-center">
        <GlobalFilter onInput={handleGlobalFilterInput} />
      </Col>
      <Col className="d-flex justify-content-end">
        <RefreshLoadingButton isLoading={props.companiesDataFetching} onClick={props.onDataRefreshClick} />
      </Col>
    </Row>
  );

  const actionBodyTemplate = (rowData: Region) => {
    return (
      <ButtonGroup variant="outlined">
        {userCanDeleteCompany && (
          <DeleteConfirmationButton
            confirmationDialogHeader={t("common.deleteConfirmationShort", {
              val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
              transform: "lowercase",
            })}
            confirmationDialogMessage={t("common.deleteConfirmation", {
              val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
              transform: "lowercase",
            })}
            onConfirmDeleteButtonClick={() => handleConfirmDeleteCompanyClick(rowData.id)}
          />
        )}
        {
          <Button
            variant="contained"
            component={Link}
            to={{
              pathname: sitesViewerPath?.replace(":companyName", rowData.name.toString()),
            }}
          >
            {altRegionNames.data?.sitesAltName ?? t("common.sites")}
          </Button>
        }
      </ButtonGroup>
    );
  };

  if (props.companiesDataLoading) return <DataTableSkeleton />;
  else
    return (
      <React.Fragment>
        <div className="datatable-responsive">
          <DataTable
            value={props.companiesData}
            header={headerToolbar}
            // If the global filter is an empty string add a space, otherwise some results may get filtered out
            globalFilter={globalFilter === "" ? " " : globalFilter}
            filterDisplay="row"
            removableSort
            sortMode="multiple"
            className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
            emptyMessage={t("common.emptyMessage", {
              val: altRegionNames.data?.companiesAltName ?? "$t(common.companies)",
            })}
            loading={props.companiesDataLoading}
            multiSortMeta={multiSortMeta}
            onSort={onSort}
          >
            <Column
              field={"name"}
              header={t("settings.companyName", {
                val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
              })}
              body={(rowData: Region) => (
                <TextTemplate
                  rowData={rowData}
                  field={"name"}
                  header={t("settings.companyName", {
                    val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
                  })}
                />
              )}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="name"
            />
            {
              <Column
                body={actionBodyTemplate}
                headerStyle={{ width: "8em", textAlign: "center" }}
                bodyStyle={{ textAlign: "center", overflow: "visible" }}
                header={t("common.actions")}
              />
            }
          </DataTable>
        </div>

        <Snackbar open={showSnackbar} onClose={handleErrorSnackbarClose}>
          <Alert severity={snackbarSeverity} variant="filled">
            {snackbarText}
          </Alert>
        </Snackbar>
      </React.Fragment>
    );
}
